import styled, { css } from 'styled-components';
import { forwardRef } from 'react';
import { useRouter } from 'next/router';
import getConfig from 'next/config';

import { config } from '_config';
import { ICONS_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';

import PrivacySection from '_components/composed/sections/PrivacySection';
import AboutBannerSection from '_components/composed/sections/AboutBannerSection';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';

const { publicRuntimeConfig } = getConfig();

const StyledPrivacyPage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCircle = styled(forwardRef(({ size, rotate, top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;

  background: ${`linear-gradient(180deg, ${theme.colors.blue[500]} 0%, rgba(46, 144, 250, 0) 100%)`};
  border-radius: 50%;
  opacity: 0.1;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  ${({ rotate }) =>
    rotate &&
    css`
      transform: ${`rotate(${rotate}deg)`};
    `}

  ${({ size }) =>
    size &&
    css`
      width: ${size}px;
      height: ${size}px;
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledImageContainer = styled(forwardRef(({ top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

const PrivacyPage = () => {
  const router = useRouter();
  return (
    <StyledPrivacyPage>
      <AboutBannerSection
        header="Privacy Policy"
        imgSrc={`${config.basePath}/images/terms.svg`}
      />
      <PrivacySection />
      <BottomBannerSection
        header={<>Don&#39;t wait, Protect Your Brand now</>}
        footer={
          <Button
            suffix={
              <Icon
                alias={ICONS_ALIASES.arrowRight}
                color={theme.colors.blue[500]}
              />
            }
            color={theme.colors.white}
            onClick={() => router.push(ROUTES.trademarkRegistration)}
            textColor={theme.colors.blue[500]}
            hoverColor={theme.colors.white}
            hoverTextColor={theme.colors.blue[500]}
          >
            Start Registering Now
          </Button>
        }
        imgSrc={`${config.basePath}/images/trademark-registration-bottom.svg`}
        imgTopDesktopPosition="-130px"
        moreSpace
        disclaimer
      />
      <StyledCircle size={686} rotate={-90} left={-63} top={-199} />
      <StyledImageContainer left={-44} top={285}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledCircle
        size={686}
        rotate={-90}
        left={-232}
        top={'calc(30% - 60px)'}
      />
      <StyledImageContainer left={65} top={'calc(34% + 30px)'}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledCircle size={686} rotate={90} right={-199} top={155} />
      <StyledImageContainer right={-66} top={78}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
    </StyledPrivacyPage>
  );
};

export default PrivacyPage;
